'use client';
import { useState, useEffect, useRef } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { Heart, Users, Rocket, LogOut, ChevronDown, DollarSign, Loader2 } from 'lucide-react';
import UserDefaultBg from '@/app/assets/image/user/bg.png';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@/app/stores/useUserStore';
import { usePrivy } from '@privy-io/react-auth';
import { type UserProfile } from '@/lib/api';
import { chainList, hyperliquidChain, predictChain } from '@/app/const/chain';
import { api } from '@/lib/api';
import { ChainInfo, getBalancesByAddress, TokenBalance } from '@/lib/dex-api';
import { getClearinghouseState } from '@/lib/hl-api';
import { Skeleton } from '@/components/ui/skeleton';
import CopyText from '@/components/copy';
import RankingBg from '@/app/assets/image/dark/rank-bg.png';
import CoTypography from '@/components/co-typography';
import { useSafeAddress } from '@/app/predict/hooks/use-safe-address';
import { predictApi } from '@/lib/predict-api';
import { Tooltip as TooltipAntd, message, Select, Popover } from 'antd';
import { Button } from '@/components/ui/button';
import UserAvatar from '@/app/components/User/Avatar';
import { VipUserIcon, WalletActiveIcon, LiveCenterIcon, WalletIcon, UserCenterIcon, TwoFAIcon } from '@/app/const/svg';
import { Twitter } from '@/components/main-icons';
import { toast } from '@/hooks/use-toast';
import { useIsMobile } from '@/components/ui/use-mobile';
import { useNavigate } from 'react-router-dom';
import DepositV2 from '../exchange/depositV2';
import UserLevel from '@/app/components/User/Level';
import UserTags from '@/app/components/User/Tags';
import BigNumber from 'bignumber.js';
import { usePredictStore, usePredictCashBalance } from '@/app/stores/usePredictStore';
import { goToLogin } from '@/lib/login';
import TwoFaModal from '@/components/TwoFaModal';
import CoInvite from './co-invite';
import RiskModal from '../risk-modal';
import Chain from '../chain';
import { formatPriceWithoutComma } from '@/app/perps/util';

export default function CoUser({ isLive = false }: { isLive?: boolean }) {
  // 关键字
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { clearUserInfo, userInfo, evmAddress, solanaAddress } = useUserStore();
  // 资产相关入口统一走充值弹窗 DepositV2（现货 / 合约 / 预测）
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOperationType, setModalOperationType] = useState<'spot' | 'perps' | 'predict'>('spot');

  const { logout: privyLogout } = usePrivy();
  const [balance, setBalance] = useState<string>('0.00');
  const [hlBalance, setHlBalance] = useState<any>({});
  // 默认arbitrum
  const [chainId, setChainId] = useState<number>(42161);
  const [isChainSelectOpen, setIsChainSelectOpen] = useState(false);
  // 当前高亮的币种数据，默认arbitrum
  const [activeCoinData, setActiveCoinData] = useState<ChainInfo | undefined>(chainList.find(item => item.chainId === chainId) as ChainInfo);
  const [loading, setLoading] = useState<boolean>(false);
  const [perpsLoading, setPerpsLoading] = useState<boolean>(false);
  // Predict 账户数据
  // - cashBalance 从 usePredictStore 读(全局单例,V1=USDC.e / V2=pUSD,跟 /portfolio/predict 完全一致)
  // - portfolioValue(持仓估值)单独 fetch,与 /portfolio/predict 同源(Polymarket Data API)
  const { safeAddress } = useSafeAddress();
  const cashBalance = usePredictCashBalance();
  const refreshBalances = usePredictStore(s => s.refreshBalances);
  const [portfolioValue, setPortfolioValue] = useState<number>(0);
  const [predictLoading, setPredictLoading] = useState<boolean>(false);
  // 总估值 = 现金 + 持仓价值,跟 /portfolio/predict 的 Portfolio value 一致
  const predictBalance = cashBalance + portfolioValue;
  // twitter授权状态
  const [isTwitterAuthLoading, setIsTwitterAuthLoading] = useState(false);
  // 2FA 绑定状态，仅 data.bound 为 true 时显示已绑定图标
  const [twoFaBound, setTwoFaBound] = useState<boolean | null>(null);
  const [twoFaSetupOpen, setTwoFaSetupOpen] = useState(false);
  // 风险弹窗
  const [riskModalOpen, setRiskModalOpen] = useState(false);

  const openDepositOrRiskModal = (type: 'spot' | 'perps' | 'predict') => {
    if (userInfo?.isWalletBlacklisted) {
      setRiskModalOpen(true);
    } else {
      setModalOperationType(type);
      setModalOpen(true);
    }
  };
  // 个人信息
  const [userProfile, setUserProfile] = useState<UserProfile>({
    userId: 0,
    name: '',
    username: '-',
    avatar: '',
    banner: UserDefaultBg as unknown as string,
    bio: '-',
    twitter: '',
    badge: '',
    followers: '0',
    following: '0',
    userPosts: 0,
    joinDate: 'xx年xx月',
    location: 'xx',
    website: '',
    isStreamer: 0,
    invitationCode: '',
    walletAddress: '',
    walletNetwork: '',
    totalBalance: 0,
    hlBalance: 0,
    pointsUserList: [],
    consumeLevel: 1,
    lighted: false,
  });

  // 退出登录
  const handleLogout = async () => {
    if (isLive && process.env.isProd) {
      message.warning({
        content: (
          <span className="text-sm">
            {t('me.logoutWhileLiveWarning')}{' '}
            <b className="text-orange-500 cursor-pointer hover:underline" onClick={() => navigate('/stream')}>
              {t('me.liveRoom')}
            </b>{' '}
            {t('me.logoutAfterCloseLive')}
          </span>
        ),
        duration: 5,
      });
      return;
    }
    try {
      // 退出  Privy
      await privyLogout();

      // 清除本地缓存
      await clearUserInfo();

    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使出错也跳转到登录页
      goToLogin();
    }
  };
  const getSpotBalance = async () => {
    setLoading(true);
    // 获取地址
    const currentWalletAddress = chainId === 501 ? userInfo?.solWalletAddress : userInfo?.evmWalletAddress;
    // 获取余额
    const tokenBalances: TokenBalance[] = await getBalancesByAddress({
      chains: chainId.toString() || '',
      address: currentWalletAddress || '',
    });
    let totalBalance = new BigNumber(0);
    tokenBalances.forEach((item: any) => {
      const balance = new BigNumber(item.balance);
      const tokenPrice = new BigNumber(item.tokenPrice);
      totalBalance = totalBalance.plus(balance.multipliedBy(tokenPrice));
    });
    // 如果是0的话，不保留小数点
    if (totalBalance.isZero()) {
      setBalance('0.00');
    } else {
      setBalance(totalBalance.toFixed(2));
    }
    setLoading(false);
  };

  const getHlBalance = async () => {
    setPerpsLoading(true);
    const hlBalance: any = await getClearinghouseState(userInfo?.evmWalletAddress || '');
    setHlBalance(hlBalance);
    setPerpsLoading(false);
  };

  /** 获取 Predict 总估值:cash(走 store,自动按 version 选 USDC.e/pUSD) + 持仓价值 */
  const getPredictBalance = async () => {
    if (!safeAddress) return;
    setPredictLoading(true);
    try {
      // 并行触发:刷新 store 余额(V1/V2 双余额)+ 拉持仓价值(predictBalance 自动派生 = cash + portfolio)
      const [, value] = await Promise.all([
        refreshBalances(safeAddress),
        predictApi.getUserValue(safeAddress).catch(() => 0),
      ]);
      setPortfolioValue(value);
    } catch (error) {
      console.error('获取 Predict 余额失败:', error);
    } finally {
      setPredictLoading(false);
    }
  };

  const getTwoFaStatus = async () => {
    const { data } = await api.user.getTwoFaStatus();
    setTwoFaBound(data?.bound ?? false);
  };

  // 获取全部余额
  const getUserBalance = async () => {
    Promise.all([getSpotBalance(), getHlBalance(), getPredictBalance(), getTwoFaStatus()]);
  };

  const didInitBalanceRef = useRef(false);

  // Twitter授权处理
  const handleTwitterAuth = async () => {
    setIsTwitterAuthLoading(true);
    try {
      // 当前页面地址
      const redirectUri = `${window.location.origin}/twitter-callback/`;
      const twitterRes = await api.user.getTwitterAuthUrl({
        redirectUri,
      });
      const { authorizationUrl } = twitterRes?.data || {};
      if (authorizationUrl) {
        window.location.href = authorizationUrl;
      }
    } catch (error) {
      console.error('Twitter授权启动失败:', error);
      setIsTwitterAuthLoading(false);
      toast({
        title: t('Me.profile.twitter_auth_failed'),
        description: t('Me.profile.twitter_auth_start_failed'),
        variant: 'destructive',
      });
    }
  };

  // 路由切换
  const handleTabChange = (path: any) => {
    if (!isMobile) {
      window.open(`${path}`, '_blank');
    }
  };

  // 跳转到资产中心对应 tab
  const navigateToPortfolio = (accountType: 'spot' | 'perps' | 'predict') => {
    navigate(`/portfolio/${accountType}`);
  };
  // 首次加载：拉取全部余额；后续 chainId 变化仅拉取现货余额
  useEffect(() => {
    if (!chainId) return;
    if (didInitBalanceRef.current) {
      getSpotBalance();
    } else {
      didInitBalanceRef.current = true;
      getUserBalance();
    }
  }, [chainId]);

  // 个人信息是否更新 - 直接使用全局缓存的用户信息
  useEffect(() => {
    if (userInfo && userInfo.name && userInfo.userId) {
      setUserProfile(userInfo as unknown as UserProfile);
    }
  }, [userInfo]);

  const renderUserBaseInfo = () => {
    return (
      <div
        className="transition-all flex items-center cursor-pointer gap-1 rounded-md bg-background-card border border-button-border/85 relative p-2 hover:border-button-border text-font-secondary hover:text-font-primary h-[34px] line-0 hover:bg-secondary-hover/50 max-md:px-1"
        onClick={() => {
          if (isMobile) {
            navigate(`/${userProfile.handle}`);
          }
        }}
      >
        <div className="relative">
          <UserAvatar userInfo={userProfile} toAction={false} size={6} />
          <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex items-center justify-center">
            <UserLevel size="xs" level={userProfile?.consumeLevel} isDarkMode={!userProfile?.lighted} hasIcon={false} />
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <TooltipAntd title={userProfile.name}>
            <span className="text-[10px] text-font-primary flex-1 overflow-hidden text-ellipsis whitespace-nowrap max-w-[80px]">
              {userProfile.name}
            </span>
          </TooltipAntd>
          <div className="flex items-center gap-1 -mb-1.5 -mt-1">
            <span className="text-[9px] co-number-bold text-font-secondary flex items-center gap-1">
              <Heart className="w-2.5 h-2.5" fill="currentColor" /> {userProfile.following}
            </span>
            •
            <span className="text-[9px] co-number-bold text-font-secondary flex items-center gap-1">
              <Users className="w-2.5 h-2.5" fill="currentColor" /> {userProfile.followers}
            </span>
          </div>
        </div>
        <div className="hidden md:flex relative bg-background-3/5 dark:bg-background-3/20 rounded-full px-2 h-5 text-xs flex items-center justify-center gap-2 ml-2 text-font-secondary dark:text-font-primary/90">
          <WalletActiveIcon className="w-3 h-3" />
          <div className="w-[0.5px] h-3 bg-background-3/50"></div>
          <div className="flex items-center">
            <Chain {...(activeCoinData as ChainInfo)} width={12} height={12} showName={false} />
            <div className="font-bold co-number-1 flex items-center gap-0.5 h-4 ml-1">
              <DollarSign className="w-2 h-2" />
              <span className="w-[34px]">{balance}</span>
            </div>
          </div>
          <div className="flex items-center">
            <Chain {...hyperliquidChain} width={12} height={12} showName={false} />
            <div className="font-bold co-number-1 flex items-center gap-0.5 h-4 ml-1">
              <DollarSign className="w-2 h-2" />
              <span className="w-[34px]">
                {formatPriceWithoutComma({ price: Number(hlBalance?.crossMarginSummary?.accountValue || 0), decimal: 2, defaultZero: '0.00' })}
              </span>
            </div>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 max-md:hidden" />
      </div>
    );
  };
  if (isMobile) {
    return renderUserBaseInfo();
  }
  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        arrow={false}
        onOpenChange={open => {
          if (open && userProfile.userId && chainId) {
            getUserBalance();
          }
        }}
        classNames={{
          container: '!p-0 !bg-popover !border !border-button-border/50'
        }}
        content={
          <div className="p-2 relative group pumpkin-dropdown-menu">
            {userProfile.banner && (
              <div className="absolute inset-0 bottom-0 dark:opacity-10 opacity-5 blur-sm pointer-events-none">
                <div
                  style={{
                    backgroundImage: `url(${userProfile.banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '100%',
                  }}
                  className="user-bg"
                />
              </div>
            )}
            <div className="flex items-center gap-3 px-2 py-1.5">
              <div className="relative">
                <UserAvatar userInfo={userProfile} toAction={false} size={12} />
                <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex items-center justify-center">
                  <UserLevel level={userProfile?.consumeLevel} isDarkMode={!userProfile?.lighted} className="border border-popover" />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <TooltipAntd title={userProfile.name}>
                    <h3 className="max-w-[200px] text-md font-medium line-clamp-1 text-font-primary break-all">{userProfile.name}</h3>
                  </TooltipAntd>
                  <span className="text-xs text-font-secondary flex items-center">
                    (ID: {userProfile.userId}
                    <div className="ml-1">
                      <CopyText text={userProfile.userId?.toString() || ''} className="text-xs" color="#909090" />
                    </div>
                    )
                  </span>
                  <UserTags userLabels={userProfile?.userLabels} size="5" />
                  {!!userProfile.isOfficial && (
                    <TooltipAntd title={t('liveDetail.officialVerified')}>
                      <VipUserIcon className="size-4 text-primary cursor-pointer" />
                    </TooltipAntd>
                  )}
                </div>
                <div className="flex items-center gap-2 text-font-primary co-number-bold">
                  <span
                    className="text-xs  flex items-center gap-1 opacity-80 cursor-pointer hover:opacity-100"
                    onClick={() => handleTabChange(`/${userProfile.handle}/follow`)}
                  >
                    <Heart className="w-2.5 h-2.5 text-red-500" fill="currentColor" />
                    {t('explore.followUser')} {userProfile.following}
                  </span>
                  •
                  <span
                    className="text-xs text-font-primary co-number-bold flex items-center gap-1 opacity-80 cursor-pointer hover:opacity-100"
                    onClick={() => handleTabChange(`/${userProfile.handle}/fans`)}
                  >
                    <Users className="w-2.5 h-2.5 text-orange-500" fill="currentColor" />
                    {t('explore.fansUser')} {userProfile.followers}
                  </span>
                  •
                  {userProfile.twitter ? (
                    <TooltipAntd title={userProfile.twitter}>
                      <a
                        className="text-blue-400 flex items-center gap-1 text-xs cursor-pointer"
                        href={`https://x.com/${userProfile.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Twitter size="12" fill="currentColor" />
                        {t('me.twitterVerified')}
                      </a>
                    </TooltipAntd>
                  ) : (
                    <TooltipAntd title={t('Me.profile.click_to_auth_twitter')}>
                      <div className="text-font-secondary flex items-center gap-1 text-xs cursor-pointer" onClick={handleTwitterAuth}>
                        {isTwitterAuthLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Twitter size="12" fill="currentColor" />}
                        {isTwitterAuthLoading ? t('Me.profile.twitter_auth_loading') : t('me.twitterUnverified')}
                      </div>
                    </TooltipAntd>
                  )}
                </div>
              </div>
            </div>
            <CoInvite className="m-2" size="sm" closeable={userInfo?.applyInvite?.applicationStatus === 2} />
            <div className="my-2 min-w-[360px] flex flex-col rounded-md bg-background-card min-h-20 p-2 backdrop-blur-sm group relative overflow-visible border border-button-border/50 mx-2">
              <div
                className="absolute inset-0 group-hover:scale-110 transition-all duration-300"
                style={{
                  backgroundImage: `url(${RankingBg})`,
                  backgroundSize: '140px 120px',
                  backgroundPosition: 'right bottom',
                  backgroundRepeat: 'no-repeat',
                }}
              ></div>
              <div className="flex flex-col items-start justify-between">
                <div
                  className="flex flex-col gap-4 rounded-md p-2 pr-0 cursor-pointer flex-1 relative w-full hover:bg-secondary-hover/30 transition-all duration-200"
                  onClick={e => {
                    if (isChainSelectOpen) return;
                    navigateToPortfolio('spot');
                  }}
                >
                  <div className="flex gap-2 items-center justify-between">
                    <CoTypography hoverMsg={t('me.spotAccountTooltip')}>
                      <span className="text-xs text-font-secondary co-typography-dashed pb-1">{t('me.spotAccount')}</span>
                    </CoTypography>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <div className="h-6 min-w-[110px]">
                      <Select
                        value={chainId}
                        onChange={value => {
                          setChainId(Number(value));
                          setActiveCoinData(chainList.find(item => item.chainId === Number(value)) as ChainInfo);
                        }}
                        options={chainList.map(item => ({
                          label: <Chain {...item} width={16} height={16} showName={true} className="w-[88px]" />,
                          value: item.chainId,
                        }))}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        onOpenChange={open => setIsChainSelectOpen(open)}
                        className="h-6 w-auto min-w-[110px]"
                        style={{
                          paddingLeft: 0,
                        }}
                        variant="borderless"
                        popupMatchSelectWidth={false}
                        placement="bottomLeft"
                      />
                    </div>
                    <div className="text-xl font-bold text-font-primary co-number-1 flex items-center gap-0.5 h-6">
                      <span className="text-xs">≈</span>
                      <DollarSign className="w-3 h-3" />
                      {loading ? <Skeleton className="h-6 w-20 bg-background-3/10" /> : balance}
                    </div>
                    <div className="absolute bottom-0 right-0 flex items-center justify-center gap-2">
                      <Button
                        onClick={e => {
                          e.stopPropagation();
                          openDepositOrRiskModal('spot');
                        }}
                        size="xs"
                      >
                        {t('exchange.deposit')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 -mt-2">
                    <div className="flex items-center gap-2 bg-secondary-hover/50 p-2 px-3 rounded-lg flex-1 justify-between hover:bg-secondary-hover/100 transition-all duration-300">
                      <div className="flex flex-col gap-1 text-font-secondary">
                        <span className="text-xs co-number-bold">EVM:</span>
                        <div className="flex items-center gap-1">
                          <CopyText text={evmAddress || ''} showText className="text-xs" svgClassName="!w-2.5 !h-2.5" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-secondary-hover/50 p-2 px-3 rounded-lg flex-1 justify-between hover:bg-secondary-hover/100 transition-all duration-300">
                      <div className="flex flex-col gap-1 text-font-secondary">
                        <span className="text-xs co-number-bold">Solana:</span>
                        <div className="flex items-center gap-1">
                          <CopyText text={solanaAddress || ''} showText className="text-xs" svgClassName="!w-2.5 !h-2.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="flex flex-col gap-4 rounded-md p-2 pr-0 cursor-pointer flex-1 relative w-full hover:bg-secondary-hover/30 transition-all duration-200"
                  onClick={() => navigateToPortfolio('perps')}
                >
                  <div className="flex gap-2 items-center gap-2">
                    <CoTypography hoverMsg={t('header.contractAccountTooltip')}>
                      <span className="text-xs text-font-secondary co-typography-dashed pb-1">{t('header.contractAccount')}</span>
                    </CoTypography>
                    {/* <span className='text-[10px] text-font-primary/50 mt-1'>{t('me.transferFromSpotAccount')}</span> */}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-font-primary co-number-1 flex items-center gap-0.5 h-6">
                      <Chain {...hyperliquidChain} chainName="Hyperliquid" width={16} height={16} showName={true} />
                      <span className="text-xs ml-2">≈</span>
                      <DollarSign className="w-3 h-3" />
                      {perpsLoading ? (
                        <Skeleton className="h-6 w-20 bg-background-3/10" />
                      ) : (
                        <div className="flex items-center gap-1">{Number(hlBalance?.crossMarginSummary?.accountValue).toFixed(2)}</div>
                      )}
                    </div>
                    <div className="absolute bottom-2.5 right-0 flex items-center justify-center gap-2">
                      <div className="absolute bottom-0 right-0 flex items-center justify-center gap-2">
                        <Button
                          onClick={e => {
                            e.stopPropagation();
                            openDepositOrRiskModal('perps');
                          }}
                          size="xs"
                        >
                          {t('exchange.deposit')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Predict Account */}
                <div
                  className="flex flex-col gap-4 rounded-md p-2 pr-0 cursor-pointer flex-1 relative w-full hover:bg-secondary-hover/30 transition-all duration-200"
                  onClick={() => navigateToPortfolio('predict')}
                >
                  <div className="flex gap-2 items-center">
                    <CoTypography hoverMsg={t('header.predictAccountTooltip')}>
                      <span className="text-xs text-font-secondary co-typography-dashed pb-1">{t('Me.portfolio.predict')}</span>
                    </CoTypography>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-font-primary co-number-1 flex items-center gap-0.5 h-6">
                      <Chain {...predictChain} chainName="Polymarket" width={16} height={16} showName={true} />
                      <span className="text-xs ml-2">≈</span>
                      <DollarSign className="w-3 h-3" />
                      {predictLoading ? (
                        <Skeleton className="h-6 w-20 bg-background-3/10" />
                      ) : (
                        <div className="flex items-center gap-1">{predictBalance.toFixed(2)}</div>
                      )}
                    </div>
                    <div className="absolute bottom-2.5 right-0 flex items-center justify-center gap-2">
                      <div className="absolute bottom-0 right-0 flex items-center justify-center gap-2">
                        <Button
                          onClick={e => {
                            e.stopPropagation();
                            openDepositOrRiskModal('predict');
                          }}
                          size="xs"
                        >
                          {t('exchange.deposit')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div 
                className="relative flex cursor-pointer items-center gap-2 rounded-sm py-1 px-2 text-sm outline-none transition-colors hover:bg-background-3/10 hover:text-font-primary" 
                onClick={() => handleTabChange(`${userProfile.handle}`)}
              >
                <div className="flex items-center mr-1 bg-secondary-hover p-2 rounded-lg">
                  <UserCenterIcon className="h-4 w-4" />
                </div>
                <span>{t('me.user')}</span>
              </div>
              <div 
                className="relative flex cursor-pointer items-center gap-2 rounded-sm py-1 px-2 text-sm outline-none transition-colors hover:bg-background-3/10 hover:text-font-primary" 
                onClick={() => handleTabChange('/portfolio/spot')}
              >
                <div className="flex items-center mr-1 bg-secondary-hover p-2 rounded-lg">
                  <WalletIcon className="h-4 w-4" />
                </div>
                <span>{t('header.assetCenter')}</span>
              </div>
              {userProfile.isStreamer === 1 ? (
                <div 
                  className="relative flex cursor-pointer items-center gap-2 rounded-sm py-1 px-2 text-sm outline-none transition-colors hover:bg-background-3/10 hover:text-font-primary" 
                  onClick={() => handleTabChange('/livecenter/')}
                >
                  <div className="flex items-center mr-1 bg-secondary-hover p-2 rounded-lg">
                    <LiveCenterIcon className="h-4 w-4" />
                  </div>
                  <span>{t('me.myLive')}</span>
                </div>
              ) : (
                <div 
                  className="relative flex cursor-pointer items-center gap-2 rounded-sm py-1 px-2 text-sm outline-none transition-colors hover:bg-background-3/10 hover:text-font-primary" 
                  onClick={() => handleTabChange('/me/form/')}
                >
                  <div className="flex items-center mr-1 bg-secondary-hover p-2 rounded-lg">
                    <Rocket className="h-4 w-4" />
                  </div>
                  <span>{t('me.becomeBroadcaster')}</span>
                </div>
              )}
              <div
                className="relative flex cursor-pointer items-center gap-2 rounded-sm py-1 px-2 text-sm outline-none transition-colors hover:bg-background-3/10 hover:text-font-primary" 
                onClick={() => {
                  if (!twoFaBound) {
                    setTwoFaSetupOpen(true);
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex items-center mr-1 bg-secondary-hover p-2 rounded-lg">
                    <TwoFAIcon className="h-4 w-4" />
                  </div>
                  <span>2FA</span>
                </div>
                {twoFaBound && <CheckCircleFilled className="h-[14px] w-[14px] text-primary mr-[12px]" />}
              </div>
            </div>
            <div className="h-[0.5px] mx-2 my-2 bg-button-border opacity-70" />
            <div
              className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 text-sm outline-none transition-colors py-1 text-red-500 hover:bg-red-500/10 hover:text-red-500"
              onClick={() => handleLogout()}
            >
              <div className="flex items-center mr-1 bg-red-500/20 p-2 rounded-lg">
                <LogOut className="h-4 w-4" />
              </div>
              <span>{t('me.loginOut')}</span>
            </div>
          </div>
        }
      >
        {renderUserBaseInfo()}
      </Popover>
      {modalOpen && <DepositV2 source="user_panel" open={modalOpen} depositTab={modalOperationType} onClose={() => setModalOpen(false)} />}
      {/* 2FA 弹窗 */}
      <TwoFaModal open={twoFaSetupOpen} onClose={() => setTwoFaSetupOpen(false)} onSuccess={() => setTwoFaBound(true)} mode="bind" />
      {/* 风险弹窗 */}
      <RiskModal open={riskModalOpen} onClose={() => setRiskModalOpen(false)} />
    </>
  );
}
